import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface ConnectorResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: {
    id: string;
    provider: string;
    name: string;
    status: string;
    createdAt: string;
  };
}

export interface ConnectorItem {
  id: string;
  provider: string;
  name: string;
  status: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string | null;
}

export interface ConnectorListResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: ConnectorItem[];
}

export type DatabaseEngine = 'PostgreSQL' | 'MySQL' | 'SqlServer';

export interface ConnectDatabaseRequest {
  engine: DatabaseEngine;
  name?: string;
  connectionString?: string;
  host?: string;
  port?: number;
  database?: string;
  username?: string;
  password?: string;
}

export interface ConnectGitHubRequest {
  name?: string;
  appId: string;
  installationId: string;
  organizationName: string;
  privateKeyPem: string;
  apiBaseUrl?: string;
}

export interface DriveItem {
  id: string;
  name: string;
  driveType: string;
  webUrl: string;
}

export interface SharePointItem {
  id: string;
  name: string;
  path: string;
  isFolder: boolean;
  size: number;
  driveId: string;
  mimeType: string | null;
}

export interface DrivesResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: DriveItem[];
}

export interface ItemsResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: {
    driveId: string;
    folderId: string;
    count: number;
    items: SharePointItem[];
  };
}

export interface ProcessedSharePointDocumentsResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: {
    total: number;
    items: { sharePointItemId: string }[];
  };
}

export interface SharePointFileTypeCountItem {
  fileType: string;
  count: number;
  percentage: number;
}

export interface SharePointFileTypeCountsData {
  libraryUrl: string;
  total: number;
  items: SharePointFileTypeCountItem[];
}

export interface SharePointFileTypeCountsResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: SharePointFileTypeCountsData;
}

export interface SharePointFileSizeBucket {
  fileType: string;
  count: number;
  sizeBytes: number;
}

export interface SharePointFileSizeStatsData {
  unprocessedBytes: number;
  failedBytes: number;
  processedBytes: number;
  unprocessedByType: SharePointFileSizeBucket[];
  failedByType: SharePointFileSizeBucket[];
  processedByType: SharePointFileSizeBucket[];
}

export interface SharePointFileSizeStatsResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: SharePointFileSizeStatsData;
}

export interface UnprocessedFilesResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: {
    unprocessedTotal: number;
    total: number;
    page: number;
    pageSize: number;
    items: SharePointItem[];
  };
}

export interface DatabaseTablesResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: {
    id: string;
    name: string;
    provider: string;
    tables: string[];
  };
}

export interface DatabaseSyncResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: {
    connectionName: string;
    found: boolean;
    updated: string[];
    added: string[];
  };
}

// ── Document source connectors (Google Drive, FTP, ...) ─────────────────────
// The backend describes each source's settings, so one form serves every source.

export interface ApiEnvelope<T> {
  statusCode: number;
  success: boolean;
  message: string;
  data: T;
}

export type SettingFieldType = 'text' | 'password' | 'textarea' | 'number' | 'bool' | 'select';

export interface SettingField {
  key: string;
  label: string;
  type: SettingFieldType;
  required: boolean;
  secret: boolean;
  default: string | null;
  options: string[] | null;
}

export interface SourceType {
  provider: string;
  settings: SettingField[];
}

/** What the API returns in place of a saved secret, and accepts back as "keep it". */
export const SECRET_PLACEHOLDER = '********';

/**
 * Providers configured through the generic connector form. SharePoint is also served by the
 * generic API, but keeps its own card and browser (/documents) for now.
 */
export const GENERIC_SOURCE_PROVIDERS: Record<string, { label: string; icon: string }> = {
  GoogleDrive: { label: 'Google Drive', icon: 'googledrive' },
  Ftp: { label: 'FTP / SFTP', icon: 'ftp' },
  Slack: { label: 'Slack', icon: 'slack' },
  Teams: { label: 'Microsoft Teams', icon: 'teams' },
};

export function sourceLabel(provider: string): string {
  return GENERIC_SOURCE_PROVIDERS[provider]?.label ?? provider;
}

export type SettingValues = Record<string, string | number | boolean>;

export interface SourceConnectorDetail extends ConnectorItem {
  settings: Record<string, string | null>;
}

export interface SourceRoot {
  id: string;
  name: string;
}

export interface SourceItem {
  id: string;
  name: string;
  path: string;
  isFolder: boolean;
  size: number;
  rootId: string;
  mimeType: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class Connector {
  private baseUrl = `${environment.apiUrl}/connectors`;

  constructor(private http: HttpClient) {}

  connectSharePoint(url: string): Observable<ConnectorResponse> {
    return this.http.post<ConnectorResponse>(`${this.baseUrl}/sharepoint`, { url });
  }

  connectDatabase(payload: ConnectDatabaseRequest): Observable<ConnectorResponse> {
    return this.http.post<ConnectorResponse>(`${this.baseUrl}/database`, payload);
  }

  connectGitHub(payload: ConnectGitHubRequest): Observable<ConnectorResponse> {
    return this.http.post<ConnectorResponse>(`${this.baseUrl}/github`, payload);
  }

  getDatabaseTables(id: string): Observable<DatabaseTablesResponse> {
    return this.http.get<DatabaseTablesResponse>(`${this.baseUrl}/database/${id}/tables`);
  }

  syncDatabaseSchema(id: string): Observable<DatabaseSyncResponse> {
    return this.http.post<DatabaseSyncResponse>(`${this.baseUrl}/database/${id}/sync`, {});
  }

  getAll(): Observable<ConnectorListResponse> {
    return this.http.get<ConnectorListResponse>(this.baseUrl);
  }
  getDrives(): Observable<DrivesResponse> {
  return this.http.get<DrivesResponse>(`${this.baseUrl}/sharepoint/drives`);
}
delete(id: string): Observable<any> {
  return this.http.delete<any>(`${this.baseUrl}/${id}`);
}
getItems(driveId: string, folderId: string = 'root', maxItems: number = 500): Observable<ItemsResponse> {
  return this.http.get<ItemsResponse>(
    `${this.baseUrl}/sharepoint/items?driveId=${driveId}&folderId=${folderId}&maxItems=${maxItems}`
  );
}
getProcessedItemIds(): Observable<Set<string>> {
  return this.http.get<ProcessedSharePointDocumentsResponse>(
    `${this.baseUrl}/sharepoint/documents/processed`,
    { params: { page: 1, pageSize: 5000 } }
  ).pipe(map(res => new Set((res.data.items || []).map(i => i.sharePointItemId))));
}

getSharePointFileTypeCounts(): Observable<SharePointFileTypeCountsResponse> {
  return this.http.get<SharePointFileTypeCountsResponse>(
    `${this.baseUrl}/sharepoint/file-type-counts`
  );
}

getSharePointFileSizeStats(): Observable<SharePointFileSizeStatsResponse> {
  return this.http.get<SharePointFileSizeStatsResponse>(
    `${this.baseUrl}/sharepoint/file-size-stats`
  );
}

getSourceTypes(): Observable<ApiEnvelope<SourceType[]>> {
  return this.http.get<ApiEnvelope<SourceType[]>>(`${this.baseUrl}/types`);
}

/** Tests settings without saving. With connectorId, masked secrets are filled from that saved connector. */
testSource(provider: string, settings: SettingValues, connectorId?: string): Observable<ApiEnvelope<{ success: boolean }>> {
  return this.http.post<ApiEnvelope<{ success: boolean }>>(`${this.baseUrl}/test`, { provider, settings, connectorId });
}

createSource(provider: string, name: string, settings: SettingValues): Observable<ConnectorResponse> {
  return this.http.post<ConnectorResponse>(this.baseUrl, { provider, name, settings });
}

getSource(id: string): Observable<ApiEnvelope<SourceConnectorDetail>> {
  return this.http.get<ApiEnvelope<SourceConnectorDetail>>(`${this.baseUrl}/${id}`);
}

updateSource(id: string, name: string, settings: SettingValues): Observable<ConnectorResponse> {
  return this.http.put<ConnectorResponse>(`${this.baseUrl}/${id}`, { name, settings });
}

getSourceRoots(id: string): Observable<ApiEnvelope<SourceRoot[]>> {
  return this.http.get<ApiEnvelope<SourceRoot[]>>(`${this.baseUrl}/${id}/roots`);
}

/** Direct children of a folder; no folderId lists the root itself. */
getSourceItems(id: string, rootId: string, folderId?: string, maxItems: number = 500): Observable<ApiEnvelope<{ items: SourceItem[]; truncated: boolean }>> {
  const params: Record<string, string | number> = { rootId, maxItems };
  if (folderId) params['folderId'] = folderId;
  return this.http.get<ApiEnvelope<{ items: SourceItem[]; truncated: boolean }>>(`${this.baseUrl}/${id}/items`, { params });
}

getSharePointUnprocessedFiles(
  page: number = 1,
  pageSize: number = 10,
  search: string = '',
  fileType: string = ''
): Observable<UnprocessedFilesResponse> {
  const params: Record<string, string | number> = { page, pageSize };
  if (search.trim()) params['search'] = search.trim();
  if (fileType.trim()) params['fileType'] = fileType.trim();
  return this.http.get<UnprocessedFilesResponse>(
    `${this.baseUrl}/sharepoint/unprocessed-files`,
    { params }
  );
}
}